import React, { useCallback, useRef } from 'react';
import { FindingStatus, type Finding } from '#/graphql/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import List from '#/common/components/List';
import { getFindingList } from '#/graphql/queries';
import { gql } from '@apollo/client';
import { withChip } from '#/common/utils/wrappers';
import { priorityItems, statusItems } from '../../utils/constants';
import { findingStatusColors, priorityColorMap } from '../FindingDataGrid/columns';
import { formatDate } from '#/common/utils/formatters';
import columns from '../FindingDataGrid/columns';
import { Filter } from '#/common/components/DataGrid';
import { renderPriorityDot } from '#/common/utils/renderPriorityDot';
import getPermanentFilterValue from '#/common/utils/parsers/parsePermanentFilters';
import useToast from '#/common/hooks/useToast';
import useFindingDelete from '../../hooks/useDeleteFindingMutation';
import { List as DxList } from 'devextreme-react/list';

const getFindingListQuery = gql(getFindingList);

export interface FindingListProps {
  hideNewAction?: boolean;
  permanentFilters?: Filter[];
}

const FindingList: React.FC<FindingListProps> = ({ hideNewAction, permanentFilters }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { confirm, alert } = useToast();
  const list = useRef<DxList | null>(null);

  const { deleteFinding } = useFindingDelete();

  const deleteItem = useCallback(
    async (items?: Finding[], clearSelectedItems?: () => void) => {
      if (!items?.length) return;

      const deletableItems = items.filter((item) => item.status === FindingStatus.new);
      const nonDeletableItems = items.filter((item) => item.status !== FindingStatus.new);

      if (!deletableItems?.length) {
        await alert(
          'warning',
          'None of the selected findings can be deleted. Only findings with "New" status can be deleted.',
        );
        return;
      }

      let confirmMessage = '';
      if (nonDeletableItems.length > 0) {
        confirmMessage = `${deletableItems.length} of ${items.length} selected findings will be deleted. Only findings with "New" status can be deleted. Continue?`;
      } else {
        confirmMessage = `Are you sure you want to delete ${deletableItems.length} finding${
          deletableItems.length > 1 ? 's' : ''
        }?`;
      }

      const sure = await confirm('error', confirmMessage);
      if (!sure) return;

      const idsToDelete = deletableItems.map((item) => item.id);
      await deleteFinding(idsToDelete.join(','));
      list.current && list.current.instance.reload();
      clearSelectedItems && clearSelectedItems();
    },
    [deleteFinding, confirm, alert],
  );

  const assetId =
    searchParams.get('assetId') ||
    getPermanentFilterValue('assetId', permanentFilters) ||
    undefined;

  const openFinding = useCallback(
    (finding: Finding) => {
      navigate(`/findings/${finding.id}`);
    },
    [navigate],
  );

  return (
    <List<Finding>
      setRef={(ref) => (list.current = ref)}
      titleKey="shortDescription"
      onClick={openFinding}
      getItemsQuery={getFindingListQuery}
      permanentFilters={permanentFilters}
      subtitleKey="displayNumber"
      customChip={(item: Finding) => {
        const statusLabel =
          statusItems.find((s) => s.key === item.status)?.label || item.status || '';
        const statusColors = item.status ? findingStatusColors[item.status] : undefined;
        return withChip(statusLabel, 'normal', statusColors);
      }}
      customHeadRight={(item: Finding) =>
        renderPriorityDot({
          item,
          priorityItems,
          priorityColorMap,
          getPriorityValue: (item) => item.priority,
        })
      }
      bodyFields={[
        {
          key: 'asset.assetTag',
          label: 'Asset',
          labelKey: 'asset',
        },
        {
          key: (item: Finding) => formatDate(item.reportedOn),
          label: 'Reported On',
        },
      ]}
      columnList={columns}
      sortingRules={[{ selector: 'updatedAt', desc: true }]}
      isActionDeleteOnly
      floatingButtonOnClick={deleteItem}
    />
  );
};

export default React.memo(FindingList);
